import React from 'react'
import { useSelector } from 'react-redux'
import { Container, Row, Col, Card } from 'react-bootstrap'
import { AccordionSection, AccordionCard } from '../../accordion'
import Tooltip from '../../tooltip'
import { PrimaryLink } from 'components/styledComponents/link'
import { formatCurrency, formatNumber } from '../../../helpers'
import ContactUsDialog from '../../dialog/contactUsDialog'
import { applicationRoles } from '../../../constants/actionTypes'
import { Tabs } from '@mmsg/louie'
import { Icon } from 'icons'
import FeesChargesWithoutTaxImplicationSection from './feesChargesWithoutTaxImplicationSection'
import FeesChargesWithTaxImplicationSection from './feesChargesWithTaxImplicationSection'

const ColItem = ({
  icon,
  iconSize,
  id,
  title,
  value,
  tooltip,
  isDisplayed = true,
}) => {
  return isDisplayed &&
    value &&
    ((!!value.incGST && !!value.exGST) ||
      (typeof value !== 'object' && !!value)) ? (
    <Col xs={12} md={6} className="mb-12">
      <Card>
        <Card.Body className="d-flex py-2 justify-content-between">
          <div className="d-flex">
            <Icon iconType={icon} size={iconSize ? iconSize : 1.4} primary />
            <div className="d-flex flex-column ml-2">
              <span className="card-body__title">{title}</span>
              {typeof value === 'object' ? (
                <>
                  <div>
                    <span className="font-weight-bold">
                      {formatCurrency(value.incGST)}
                    </span>{' '}
                    <span className="card-body__gst">(Inc GST)</span>
                  </div>
                  <div>
                    <span className="font-weight-bold">
                      {formatCurrency(value.exGST)}{' '}
                    </span>{' '}
                    <span className="card-body__gst">(Ex GST)</span>
                  </div>
                </>
              ) : (
                <span className="font-weight-bold">
                  {formatCurrency(value)}
                </span>
              )}
            </div>
          </div>
          <Tooltip
            header={tooltip.header || title}
            subHeading={tooltip.subHeading}
            text={tooltip.text}
          />
        </Card.Body>
      </Card>
    </Col>
  ) : null
}

const RegistrationInsuranceTable = ({
  rego,
  regoSubtext,
  compIns,
  compSubtext,
  id,
}) => (
  <Row>
    <ColItem
      icon="Registration"
      id={`${id}-annual-registration`}
      title="Registration"
      value={rego}
      tooltip={{
        subHeading: regoSubtext,
        text: 'To drive a car in Australia it needs to be re-registered each year. We make paying for registration easy by budgeting for it across the life of your lease. We can pay it on your behalf from your budget, or you can pay it upfront and claim it back.',
      }}
    />
    <ColItem
      icon="ComprehensiveInsurance"
      iconSize={2.2}
      id={`${id}-comprehensive-insurance`}
      title="Comprehensive Insurance"
      value={compIns}
      tooltip={{
        subHeading: compSubtext,
        text: 'All cars under a Novated Lease must be covered by Comprehensive Insurance. So you don’t have to worry about finding the funds year after year, we budget for the annual renewal over the life of your lease.',
      }}
    />
  </Row>
)

const RunningCostTable = ({
  fuelPay,
  fuelUseSubtext,
  electricityPay,
  electricityUseSubtext,
  tyresPay,
  tyresSubtext,
  roadsidePay,
  roadsideSubtext,
  maintenancePay,
  maintenanceSubtext,
  incidentalsPay,
  noOfTyresBudget,
  id,
}) => (
  <Row>
    <ColItem
      icon="Fuel"
      id={`${id}-fuel`}
      title="Fuel"
      value={fuelPay}
      tooltip={{
        subHeading: fuelUseSubtext,
        text: 'Taking into account a few factors from your lease (like your estimated kms or preferred car), we set aside a budget for you to keep filling up your car with fuel.',
      }}
    />
    <ColItem
      icon="Electricity"
      id={`${id}-electricity`}
      title="Electricity"
      value={electricityPay}
      tooltip={{
        subHeading: electricityUseSubtext,
        text: 'Taking into account a few factors from your lease (like your estimated kms or preferred car), we set aside a budget for you to keep your car’s battery charged.',
      }}
    />
    <ColItem
      icon="ScheduledMaintenance2"
      id={`${id}-scheduled-maintenance`}
      title="Scheduled servicing"
      value={maintenancePay}
      tooltip={{
        subHeading: maintenanceSubtext,
        text: 'Covers your standard servicing at manufacturer’s set intervals, either according to time or kilometres travelled.',
      }}
    />
    <ColItem
      icon="ReplacementTyres"
      id={`${id}-replacement-tyres`}
      title={`${noOfTyresBudget} Replacement tyres`}
      value={tyresPay}
      tooltip={{
        subHeading: tyresSubtext,
        text: 'Taking into account a few factors from your lease (like your estimated kms or preferred car), we set aside a budget for replacing worn out tyres. Generally, you’ll need a new set for every 40,000kms you travel.',
      }}
    />
    <ColItem
      icon="Incidentals"
      id={`${id}-incidentals`}
      title="Incidentals"
      value={incidentalsPay}
      tooltip={{
        text: 'A budget for those extra costs, like getting your car cleaned or detailed, or even topping up your oil.',
      }}
    />
    <ColItem
      icon="RoadsideAssistance"
      id={`${id}-roadside`}
      title="Roadside assistance"
      value={roadsidePay}
      tooltip={{
        subHeading: roadsideSubtext,
        text: '24/7 access to trained mechanical experts, helping get you back on the road in the event of a breakdown.',
      }}
    />
  </Row>
)

const RunningSection = ({
  data: {
    vehicle,
    compInsurancePremium,
    leaseTermYears,
    brand,
    isExGST,
    isTaxShare,
    fbtMethod,
    payFrequency,
    kmPerAnnum,
    userFirstName,
    consultantFirstName,
    costsPerPay,
    fuelPerPay,
    electricityPerPay,
    tyresPerPay,
    roadsidePerPay,
    maintenancePerPay,
    incidentalsPerPay,
    fuelPerAnnum,
    electricityPerAnnum,
    tyresPerAnnum,
    roadsidePerAnnum,
    maintenancePerAnnum,
    incidentalsPerAnnum,
    regoInsPerPay,
    regoPerPay,
    regoPerAnnum,
    compInsPerPay,
    compInsPerAnnum,
    totalChargesPerPay,
    nlFeePerPay,
    spFeePerPay,
    fbtPerPay,
    lcdaPerPay,
    erSharePerPay,
    gstOnECMPerPay,
    greenfleetPerPay,
    erAdminFeePerPay,
    nlFeePerAnnum,
    spFeePerAnnum,
    fbtPerAnnum,
    lcdaPerAnnum,
    erSharePerAnnum,
    gstOnECMPerAnnum,
    greenfleetPerAnnum,
    erAdminFeePerAnnum,
    noOfTyresBudget,
    isGov,
    fuelPricePerLitre,
    roadsideAssist,
    electricityCostPerKm,
    maintenanceProfile,
    overTheCapMethod,
    compInsuranceProvider,
    compInsurancePremiumFinanced
  },
  applicationRole,
  expandAccordion,
}) => {
  let fuelUseSubtext =
    vehicle && vehicle.id
      ? isGov &&
        `(${vehicle.fuelConsumption} litres/100kms @ ${
          fuelPricePerLitre && fuelPricePerLitre > 0
            ? '$' + fuelPricePerLitre + ' per litre'
            : null
        }) (${vehicle.fuelType})`
      : null

  let displayGSTonECM = () => {
    return (
      isExGST &&
      fbtMethod &&
      fbtMethod.toLowerCase().indexOf('ecm') != -1 &&
      overTheCapMethod &&
      overTheCapMethod.toLowerCase().indexOf('ecm') != -1
    )
  }

  let electricityUseSubtext =
    isGov && electricityCostPerKm
      ? `(Estimated at ${
          electricityCostPerKm && (electricityCostPerKm * 100).toFixed(1)
        }c per km)`
      : null
  let compInsuranceSubtext =
    compInsurancePremiumFinanced && '(Oly arranged)'
  let registrationSubtext =
    isGov && `(${leaseTermYears - 1} renewals budgeted for term of lease)`
  let replacementTyresSubtext =
    isGov && `(${noOfTyresBudget} budgeted for term of lease)`
  let erSharePerPayValue = isTaxShare ? erSharePerPay : 0
  let erSharePerAnnumValue = isTaxShare ? erSharePerAnnum : 0
  let gstOnECMPerPayValue = displayGSTonECM() ? gstOnECMPerPay : 0
  let gstOnECMPerAnnumValue = displayGSTonECM() ? gstOnECMPerAnnum : 0
  let totalTaxImplication =
    fbtPerPay +
    lcdaPerPay +
    erSharePerPayValue +
    gstOnECMPerPayValue +
    greenfleetPerPay +
    erAdminFeePerPay

  let feesChargesPerPayData = {
    nlFee: nlFeePerPay ? isExGST ? nlFeePerPay.exGST : nlFeePerPay.incGST : 0,
    spFee: spFeePerPay ? isExGST ? spFeePerPay.exGST : spFeePerPay.incGST : 0,
    fbt: fbtPerPay,
    lcda: lcdaPerPay,
    erShare: erSharePerPayValue,
    gstOnECM: gstOnECMPerPayValue,
    greenfleet: greenfleetPerPay,
    erAdminFee: erAdminFeePerPay,
    gstOnECM_isDisplayed: displayGSTonECM(),
    erShare_isDisplayed: isTaxShare,
    payFrequency: payFrequency,
    brand: brand,
  }

  let feesChargesPerAnnumData = {
    nlFee: nlFeePerAnnum ? isExGST ? nlFeePerAnnum.exGST : nlFeePerAnnum.incGST : 0,
    spFee: spFeePerAnnum ? isExGST ? spFeePerAnnum.exGST : spFeePerAnnum.incGST : 0,
    fbt: fbtPerAnnum,
    lcda: lcdaPerAnnum,
    erShare: erSharePerAnnumValue,
    gstOnECM: gstOnECMPerAnnumValue,
    greenfleet: greenfleetPerAnnum,
    erAdminFee: erAdminFeePerAnnum,
    gstOnECM_isDisplayed: displayGSTonECM(),
    erShare_isDisplayed: isTaxShare,
    payFrequency: payFrequency,
    brand: brand,
  }

  let estimateUpdateOn = useSelector(
    (state) =>
      state.individualEstimate &&
      state.individualEstimate.estimate &&
      state.individualEstimate.estimate.updatedOn
  )

  let totalFeeCharges = spFeePerPay && nlFeePerPay ? isExGST
    ? spFeePerPay.exGST + nlFeePerPay.exGST
    : spFeePerPay.incGST + nlFeePerPay.incGST : 0

  let isFeesChargesBeforeCutOffDate = false //// We have passed through that threshold wait period for the old estimate i.e. 15th Nov 2021. From here on it's always new estimate
  /*new Date(estimateUpdateOn) < 
    new Date(process.env.REACT_APP_TAX_IMPLICATION_CUT_OFF_DATE)*/

  return (
    <Container className="section print-page-break-before">
      <h4 className="text-dark mb-2">
        Here’s your budget for keeping the car on the road
      </h4>
      <p className="mb-5 font-color-indicative">
        We’ve estimated a budget for your bills and running costs. And with a
        fully maintained lease, it’ll be our job to manage it. By rolling all
        your on the road costs into your novated lease, not only could you
        access tax savings, but you’ll avoid bill shock when things like
        registration roll around.
      </p>

      {/* Running costs and maintenance: Start */}

      <Card className="mb-13rem card-shadow">
        <Card.Header className="border-bottom-0 d-flex justify-content-between">
          <div className="text-header">Running costs and servicing</div>
          <div className="text-larger font-weight-bold">
            {formatCurrency(costsPerPay)}
          </div>
        </Card.Header>
        <Card.Body>
          <AccordionSection
            readerText="Running costs and servicing"
            expandAccordion={expandAccordion}
            title="Flexible budgeting for your fuel, scheduled servicing and more. It’s always your money. You choose how you spend it and when."
            hasTabs
          >
            <p className="font-weight-bold mb-0">
              You’ve estimated you’ll drive
            </p>
            <p className="text-larger font-weight-bold" style={{color:'#FF1261'}}>
              {formatNumber(kmPerAnnum)} km/year{' '}
            </p>
            <p className="font-weight-bold mb-0">How it works:</p>
            <p className="font-color-indicative">
              We take the kilometres you’ve estimated you’ll drive, and use them
              to put together a flexible budget that kicks bill shock to the
              kerb. Think you’ll drive more? You can adjust your kilometres with
              your consultant, and they’ll adjust your budget to cover the extra
              expenses.
            </p>
            <p className="font-color-indicative">
              Drive less? Put whatever you haven't spent towards future
              deductions, or we'll pass it back once your lease wraps up. By
              paying for running costs from your salary before and after it gets
              taxed, you could save GST on all sorts of things. Like fuel, or
              even getting the car cleaned.
            </p>

            <Tabs
              data={[
                {
                  title: payFrequency,
                  content: () => (
                    <RunningCostTable
                      fuelPay={fuelPerPay}
                      fuelUseSubtext={fuelUseSubtext}
                      electricityPay={electricityPerPay}
                      electricityUseSubtext={electricityUseSubtext}
                      tyresPay={tyresPerPay}
                      tyresSubtext={replacementTyresSubtext}
                      roadsidePay={roadsidePerPay}
                      roadsideSubtext={
                        isGov && !roadsideAssist ? '(Client arranged)' : ''
                      }
                      maintenancePay={maintenancePerPay}
                      maintenanceSubtext={
                        isGov &&
                        "(As per manufacturer's scheduled logbook servicing)"
                      }
                      incidentalsPay={incidentalsPerPay}
                      id={`running-${payFrequency}`}
                      noOfTyresBudget={noOfTyresBudget}
                    />
                  ),
                },
                {
                  title: 'Annually',
                  content: () => (
                    <RunningCostTable
                      fuelPay={fuelPerAnnum}
                      fuelUseSubtext={fuelUseSubtext}
                      electricityPay={electricityPerAnnum}
                      electricityUseSubtext={electricityUseSubtext}
                      tyresPay={tyresPerAnnum}
                      tyresSubtext={replacementTyresSubtext}
                      roadsidePay={roadsidePerAnnum}
                      roadsideSubtext={
                        isGov && !roadsideAssist ? '(Client arranged)' : ''
                      }
                      maintenancePay={maintenancePerAnnum}
                      maintenanceSubtext={
                        isGov &&
                        "(As per manufacturer's scheduled logbook servicing)"
                      }
                      incidentalsPay={incidentalsPerAnnum}
                      id={`running-Annually`}
                      noOfTyresBudget={noOfTyresBudget}
                    />
                  ),
                },
              ]}
              activeIndex={0}
              equalWidth
            />
            {(!fuelPerPay ||
              !maintenancePerPay ||
              !tyresPerPay  ||
              !incidentalsPerPay ||
              !roadsidePerPay) && (
              <>
                <p className="font-weight-bold mb-2">
                  Running costs you’ve excluded
                </p>
                <p className="mb-4 font-color-indicative">
                  Based on your chat with {consultantFirstName}, here’s what
                  you’ve excluded. Don’t forget your budget is flexible{' '}
                  {userFirstName}, and you’ll get back whatever you don’t spend.
                  So, if you’d like to add anything back in, simply Contact your
                  consultant.
                </p>
                <ul className="font-color-indicative">
                  {!fuelPerPay && <li>Fuel</li>}
                  {!maintenancePerPay && 
                    <li>Scheduled servicing</li>
                  }
                  {!tyresPerPay && 
                    <li>Replacement tyres</li>
                  }
                  {!incidentalsPerPay && 
                    <li>Incidentals</li>
                  }
                  {!roadsidePerPay && 
                    <li>Roadside assistance</li>
                  }
                </ul>
              </>
            )}
            {applicationRole &&
              applicationRole !== applicationRoles.CONSULTANT && (
                <p className="font-weight-bold">
                  <ContactUsDialog
                    className="link"
                    theme="link"
                    launchText="Contact my consultant"
                    inlineText
                  />
                </p>
              )}
            {applicationRole &&
              applicationRole === applicationRoles.CONSULTANT && (
                <PrimaryLink disabled>Contact my consultant</PrimaryLink>
              )}
            <AccordionCard
              expandAccordion={expandAccordion}
              className="mb-24"
              title="What if I have money left in my account at the end of my lease?"
            >
              <p>
                Once your lease has wrapped up, we'll pass whatever you didn't
                spend in your budget back to your salary packaging provider.
                Your employer will then deduct income tax from this amount,
                before passing on the remaining balance to you.
              </p>
            </AccordionCard>
            <AccordionCard
              expandAccordion={expandAccordion}
              className="mb-24"
              title="What happens if I do more kilometres than estimated?"
            >
              <p>
                Not to worry, your budget is flexible. So, if you do more
                kilometres than you estimated, your consultant can adjust your
                budget to keep up. Drive less? We'll pass back the savings from
                whatever you don't spend once your lease wraps up.
              </p>
            </AccordionCard>
            <AccordionCard
              expandAccordion={expandAccordion}
              className="mb-24"
              title="What is not included in a novated lease?"
            >
              <p>
                A few things, like: road tolls, traffic infringements or
                accessories fitted to the car after your lease begins. Please
                ensure you contact your consultant prior to adding accessories
                fitted after the purchase date, as this may affect your lease.
              </p>
            </AccordionCard>
            <AccordionCard
              expandAccordion={expandAccordion}
              className="mb-24"
              title="What happens if I'm involved in an accident?"
            >
              <p>
                If the car can be fixed, we'll guide you through two options:
              </p>
              <p>
                1. Organise the repairs through your insurer; or – <br />
                2. If it's cheaper, we can organise the repairs directly with a
                repair centre.
              </p>
              <p>
                If the car is written off by your Comprehensive Motor Vehicle
                insurer, the insurer will either replace the car (subject to
                their terms and conditions) or pay your financier the market
                value of the car. In some cases this can still leave you out of
                pocket - particularly if the outstanding finance is higher than
                the car's market value.
              </p>
              <p>
                If you opt to purchase our Total Loss Assist Insurance, it can
                cover the gap between your comprehensive insurers total loss
                payment and either the outstanding finance amount or what you
                paid for the vehicle. The minimum amount you receive under this
                additional insurance if you are eligible to claim, is $5000.
              </p>
              <p>
                Add-on insurances are available through Oly, or from the
                provider of your choice, and can be packaged when you start your
                lease. Please speak with your consultant about adding this to
                your budget.
              </p>
            </AccordionCard>
          </AccordionSection>
        </Card.Body>
      </Card>
      {/* Running costs and maintenance: End */}

      {/* Registration and car insurance: Start */}

      <Card className="mb-13rem card-shadow">
        <Card.Header className="border-bottom-0 d-flex justify-content-between">
          <div className="text-header">Registration and car insurance</div>
          <div className="text-larger font-weight-bold">
            {formatCurrency(regoInsPerPay)}
          </div>
        </Card.Header>
        <Card.Body>
          <AccordionSection
            readerText="Registration and car insurance"
            expandAccordion={expandAccordion}
            title="Includes your renewals for registration and Comprehensive Insurance."
            hasTabs
          >
            <Tabs
              data={[
                {
                  title: payFrequency,
                  content: () => (
                    <RegistrationInsuranceTable
                      rego={regoPerPay}
                      regoSubtext={registrationSubtext}
                      compIns={compInsPerPay}
                      compSubtext={compInsuranceSubtext}
                      id={`rego-compIns-${payFrequency}`}
                    />
                  ),
                },
                {
                  title: 'Annually',
                  content: () => (
                    <RegistrationInsuranceTable
                      rego={regoPerAnnum}
                      regoSubtext={registrationSubtext}
                      compIns={compInsPerAnnum}
                      compSubtext={compInsuranceSubtext}
                      id={`rego-compIns-Annually`}
                    />
                  ),
                },
              ]}
              activeIndex={0}
              equalWidth
            />

            <p className="font-color-indicative text-small">
              Disclaimer: Your registration and insurance renewals can be
              impacted by the time of year you start, the insurance and renewal
              that came with the car.{' '}
              {compInsuranceProvider === 'Allianz' && compInsurancePremiumFinanced && compInsPerAnnum > 0 &&
                ' You have a choice of repairer with this policy.'}
            </p>
          </AccordionSection>
          <AccordionCard
            expandAccordion={expandAccordion}
            className="mb-24"
            title="Do I have to use Oly insurance?"
          >
            <p>
              No. Comprehensive Vehicle Insurance is required for all leases but
              you’re in total control when it comes to choosing your insurance.
              Let your consultant know your annual premium and they’ll work it
              into your budget. If you do decide to take out one of the options
              presented, we’ll take care of the admin for you. Meaning you can
              get back to whatever you’d rather be doing than chatting about
              insurance.
            </p>
          </AccordionCard>
        </Card.Body>
      </Card>
      {/* Registration and car insurance: End */}

      {/* Novated lease fees and charges: Start */}
      {/* Fees and charges before Tax implication CUT OFF */}
      {isFeesChargesBeforeCutOffDate && (
        <FeesChargesWithoutTaxImplicationSection
          totalCharges={totalChargesPerPay}
          feesChargesPerPayData={feesChargesPerPayData}
          feesChargesPerAnnumData={feesChargesPerAnnumData}
          expandAccordion={expandAccordion}
        />
      )}
      {/* Fees and charges after Tax implication CUT OFF */}
      {!isFeesChargesBeforeCutOffDate && (
        <FeesChargesWithTaxImplicationSection
          totalFeeCharges={totalFeeCharges}
          totalTaxImplication={totalTaxImplication}
          feesChargesPerPayData={feesChargesPerPayData}
          feesChargesPerAnnumData={feesChargesPerAnnumData}
          expandAccordion={expandAccordion}
        />
      )}
    </Container>
  )
}
export default RunningSection
